/* eslint-disable class-methods-use-this */
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Driver } from '../../domain/driver/Driver';
import { DriverDTO } from '../../domain/driver/DriverDTO';
import { DriverStatus } from '../../domain/driver/DriverStatus';
import { Project } from '../../domain/project/Project';
import { DriverMapper } from '../../mappers/driver/DriverMapper';
import { SessionRepository } from '../session/SessionRepository';
import { DriverRepository } from './DriverRepository';

export class DriverRepositoryApi implements DriverRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}
  async save({ driver, project }: { driver: Driver; project: Project }): Promise<Driver> {
    const response = await this.requests.put({
      uri: `/v2/driver/${driver.id}`,
      data: {
        ...DriverMapper.toRepository(driver),
        stops: undefined,
        // stops: driver.stops.map((stop) => stop.id),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return DriverMapper.toDomain(response.data, project.custom_fields!);
  }

  async saveStops({ driver, project }: { driver: Driver; project: Project }): Promise<Driver> {
    const response = await this.requests.put({
      uri: `/v2/driver/${driver.id}`,
      data: {
        stops: driver.stops.map((stop) => stop.id),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return DriverMapper.toDomain(response.data, project.custom_fields!);
  }

  async createDrivers({
    drivers,
    planId,
    project,
  }: {
    drivers: Array<Driver>;
    planId: string;
    project: Project;
  }): Promise<Array<Driver>> {
    const response = await this.requests.post({
      uri: `/v2/drivers?plan_id=${planId}`,
      data: drivers.map(DriverMapper.toRepository),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as DriverDTO[];
    return data.map((driver) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async lockDrivers({ drivers, project }: { drivers: Array<Driver>; project: Project }): Promise<Array<Driver>> {
    const requests = drivers.map((driver) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/driver/${driver.id}`,
          data: {
            is_locked: true,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedDrivers = response.flat(1) as DriverDTO[];

    return updatedDrivers
      .filter((driver) => driver !== null)
      .map((driver: DriverDTO) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async unlockDrivers({ drivers, project }: { drivers: Array<Driver>; project: Project }): Promise<Array<Driver>> {
    const requests = drivers.map((driver) => {
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/driver/${driver.id}`,
          data: {
            is_locked: false,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const updatedDrivers = response.flat(1) as DriverDTO[];

    return updatedDrivers
      .filter((driver) => driver !== null)
      .map((driver: DriverDTO) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async fetchDriver({ driverId, project }: { driverId: string; project: Project }): Promise<Driver> {
    const response = await this.requests.get({
      uri: `/v2/driver/${driverId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as DriverDTO;
    return DriverMapper.toDomain(data, project.custom_fields!);
  }

  async updateConstraints(driverIds: string[], constraints: Record<string, any>): Promise<void> {
    await this.requests.put({
      uri: `/v2/drivers/constraints`,
      data: {
        driver_ids: driverIds,
        constraints,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async searchDrivers({
    project,
    text,
    offset = 0,
    limit = 25,
    sort,
    status,
  }: {
    project: Project;
    text?: string;
    offset?: number;
    limit?: number;
    sort?: string;
    status?: DriverStatus;
  }): Promise<PaginatedResult<Driver>> {
    const response = await this.requests.get({
      uri: `/v2/drivers/search`,
      params: {
        project_id: project.id,
        ...(text ? { text } : {}),
        ...(sort ? { sort } : {}),
        ...(offset ? { offset } : {}),
        ...(limit ? { limit } : {}),
        ...(status ? { status } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<DriverDTO>;

    return {
      ...data,
      docs: data.docs.map((driver) => DriverMapper.toDomain(driver, project.custom_fields!)),
    };
  }

  async updateDriversStatus({
    drivers,
    status,
    project,
  }: {
    drivers: Array<Driver>;
    status: DriverStatus;
    project: Project;
  }): Promise<Array<Driver>> {
    const response = await this.requests.put({
      uri: `/v2/drivers/status`,
      data: {
        driver_ids: drivers.map((d) => d.id),
        status,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as Array<DriverDTO>;

    return data.map((driver) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async deleteDrivers({ driverIds, project }: { driverIds: string[]; project: Project }): Promise<Driver[]> {
    const requests = driverIds.map((driverId) => {
      return {
        method: this.requests.delete.bind(this.requests),
        params: {
          uri: `/v2/driver/${driverId}`,
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const deletedDrivers = response.flat(1) as DriverDTO[];

    return deletedDrivers
      .filter((driver) => driver !== null)
      .map((driver: DriverDTO) => DriverMapper.toDomain(driver, project.custom_fields!));
  }

  async optimize({ driver, project }: { driver: Driver; project: Project }): Promise<Driver> {
    const params = new URLSearchParams();

    const response = await this.requests.post({
      uri: `/v2/driver/${driver.id}/optimize?${params.toString()}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const { unassigned_stops, ...data } = response.data as any;

    return DriverMapper.toDomain(data, project.custom_fields!);
  }
}
